import "./style.scss";

import { useCompanySettings } from "hooks/useCompanySettings";
import type { Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import type { TPeriod } from "store/periodStore";
import { fetchPeriods, setActivePeriod } from "store/periodStore";
import { useAppSelector, type AppDispatch } from "store/store";
import type { TFetchStatus } from "store/types";

import PeriodDropdown from "./PeriodDropdown";
import PeriodTrigger from "./PeriodTrigger";
import { elementsIndex } from "./utils";

const DateComponent = ({
    closeTermsStatus,
    currentPeriod,
    setCurrentPeriod,
}: {
    closeTermsStatus: TFetchStatus | undefined;
    currentPeriod?: TPeriod | undefined;
    setCurrentPeriod?: Dispatch<SetStateAction<TPeriod | undefined>>;
}) => {
    const [t] = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const { allPeriods: periods } = useAppSelector((state) => state.period);
    const { getCompanySettings } = useCompanySettings();
    const usesMultiPeriods = getCompanySettings("uses_simultaneous_period");

    const sortedPeriods = useMemo(() => {
        if (usesMultiPeriods) {
            return [...periods].sort((a, b) => (a.isActive > b.isActive ? -1 : 1));
        }
        return [...periods].sort((a, b) =>
            new Date(a.periodStart).getTime() < new Date(b.periodStart).getTime() ? -1 : 1,
        );
    }, [usesMultiPeriods, periods]);

    const [currentIndex, setCurrentIndex] = useState(elementsIndex(sortedPeriods, currentPeriod?.id));
    const triggerRef = useRef<HTMLDivElement | null>(null);
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");

    useEffect(() => {
        dispatch(fetchPeriods());
    }, [closeTermsStatus]);

    useEffect(() => {
        if (currentPeriod) {
            setCurrentIndex(elementsIndex(sortedPeriods, currentPeriod?.id));
            setCurrentPeriod?.(sortedPeriods.find((item) => item.id === currentPeriod?.id));
        }
    }, [currentPeriod, sortedPeriods]);

    const handlePrev = () => {
        dispatch(setActivePeriod(sortedPeriods[currentIndex - 1].id));
        setCurrentIndex((prev) => --prev);
        setCurrentPeriod?.(sortedPeriods[currentIndex - 1]);
    };

    const handleNext = () => {
        dispatch(setActivePeriod(sortedPeriods[currentIndex + 1].id));
        setCurrentIndex((prev) => ++prev);
        setCurrentPeriod?.(sortedPeriods[currentIndex + 1]);
    };

    const handleChangePeriod = (id: number) => {
        if (id === currentPeriod?.id) {
            setOpen(false);
            return;
        }
        dispatch(setActivePeriod(id));
        setOpen(false);
    };

    const toggleOpen = useCallback(() => {
        setOpen((prev) => !prev);
        setSearch("");
    }, []);

    const handleClose = useCallback(() => {
        setOpen(false);
        setSearch("");
    }, []);

    const filteredPeriods = useMemo(() => {
        if (!search.trim()) return sortedPeriods;
        const q = search.toLowerCase();
        return sortedPeriods.filter((p) => {
            const dateStr = `${p.periodStart} - ${p.periodEnd}`.toLowerCase();
            return dateStr.includes(q);
        });
    }, [sortedPeriods, search]);

    return (
        <>
            <PeriodTrigger
                triggerRef={triggerRef}
                currentPeriod={currentPeriod}
                currentIndex={currentIndex}
                totalPeriods={sortedPeriods.length}
                usesMultiPeriods={usesMultiPeriods}
                open={open}
                t={t}
                onToggle={toggleOpen}
                onPrev={handlePrev}
                onNext={handleNext}
            />

            <PeriodDropdown
                open={open}
                anchorEl={triggerRef}
                usesMultiPeriods={usesMultiPeriods}
                sortedPeriods={sortedPeriods}
                filteredPeriods={filteredPeriods}
                currentPeriodId={currentPeriod?.id}
                search={search}
                onSearchChange={(e) => setSearch(e.target.value)}
                onClose={handleClose}
                onSelect={handleChangePeriod}
                t={t}
            />
        </>
    );
};

export default DateComponent;
